import fe_config from "constants/Configs";
import {
  getActiveOidcProvider,
  getActiveUserManager,
  OIDC_PROVIDER,
  OIDC_PROVIDER_STORAGE_KEY,
} from "utils/oidcUserManager";
import { ssoTabSync } from "utils/ssoTabSync";
import isArray from "lodash/isArray";
import keys from "lodash/keys";
import TokenManager from "utils/TokenManager";

export const PING_AUTH = "pingAuthorization";
export const SSO_RETURN_TARGET = "ssoReturnTarget";
export const SSO_VERIFY_COMPLETE = "ssoVerifyComplete";

export const setPingAuthorizationFlag = (flag = true) => {
  localStorage.setItem(PING_AUTH, flag);
};

const isInternalPath = (value = "") =>
  value.startsWith("/") && !value.startsWith("//");

export const getSsoReturnTarget = () =>
  sessionStorage.getItem(SSO_RETURN_TARGET) || "";

export const clearSsoReturnTarget = () => {
  sessionStorage.removeItem(SSO_RETURN_TARGET);
};

export const setSsoVerifyComplete = (flag = true) => {
  if (flag) {
    sessionStorage.setItem(SSO_VERIFY_COMPLETE, "true");
    return;
  }
  sessionStorage.removeItem(SSO_VERIFY_COMPLETE);
};

export const isSsoVerifyComplete = () =>
  sessionStorage.getItem(SSO_VERIFY_COMPLETE) === "true";

const getSsoPostLogoutRedirectUri = () => {
  if (!localStorage.getItem("home_link")) {
    return fe_config.PING_REDIRECT_URI;
  }

  return getActiveOidcProvider() === OIDC_PROVIDER.INTEGRATION
    ? fe_config.HUB_INTEGRATION_LOGOUT_URI
    : fe_config.HUB_REDIRECT_LOGOUT_URI;
};

export const setSsoReturnTarget = (value = "") => {
  if (value) {
    sessionStorage.setItem(SSO_RETURN_TARGET, value);
    return;
  }
  clearSsoReturnTarget();
};

export const resolveSsoReturnTarget = (location) => {
  const params = new URLSearchParams(location.search);
  const returnTo = params.get("return_to");

  if (returnTo && isInternalPath(returnTo)) {
    return returnTo;
  }

  const fromPath = location?.state?.from?.pathname || "";
  const fromHash = location?.state?.from?.hash || "";
  if (fromPath) {
    return `${fromPath}${fromHash}`;
  }

  if (
    typeof document !== "undefined" &&
    document.referrer &&
    document.referrer !== window.location.href
  ) {
    return document.referrer;
  }

  return "";
};

export const redirectToSsoReturnTarget = (history, fallback = "/login") => {
  const returnTarget = getSsoReturnTarget();
  clearSsoReturnTarget();

  if (!returnTarget) {
    history.replace(fallback);
    return;
  }

  if (isInternalPath(returnTarget)) {
    history.replace(returnTarget);
    return;
  }

  window.location.assign(returnTarget);
};

export const transformURL = (url) =>
  url
    .split("&")
    .filter((x) => x)
    .map((x) => x.split("="))
    .map((param) =>
      ["from_date", "to_date"].includes(param[0]) ? [param[0], param[1]] : param
    )
    .map((param) => {
      if (
        ["corp_entity_id", "corporate_entity_id"].includes(param[0]) &&
        (param[1] == null || param[1] === "null")
      ) {
        return null;
      } else {
        return param;
      }
    })
    .filter((param) => param)
    .map((x) => x.join("="))
    .join("&");

export const urlCheck = (url) => {
  if (url.includes("?")) {
    const prev = url.substr(0, url.indexOf("?") + 1);
    const url_to_transform = url.substr(url.indexOf("?") + 1, url.length);
    const aft = transformURL(url_to_transform);
    return prev + aft;
  }
  return url;
};

const filterArrayValue = (arr) =>
  arr.map((d) => {
    if (typeof d === "object" && !isArray(d)) {
      (d["corp_entity_id"] == null || d["corp_entity_id"] === "null") &&
        delete d["corp_entity_id"];
      (d["corporate_entity_id"] == null ||
        d["corporate_entity_id"] === "null") &&
        delete d["corporate_entity_id"];
    }
    return d;
  });

export const dataCheck = (data, isRecursive) => {
  if (!data) {
    return data;
  }
  let result;
  if (isArray(data)) {
    result = filterArrayValue(data);
  } else if (!isArray(data) && typeof data === "object") {
    result = {};
    keys(data).forEach((key) => {
      if (key === "corp_entity_id") {
        if (
          data["corp_entity_id"] != null &&
          data["corp_entity_id"] !== "null"
        ) {
          result[key] = dataCheck(data[key], true);
        }
      } else if (key === "corporate_entity_id") {
        if (
          data["corporate_entity_id"] != null &&
          data["corporate_entity_id"] !== "null"
        ) {
          result[key] = dataCheck(data[key], true);
        }
      } else {
        result[key] = dataCheck(data[key], true);
      }
    });
  }
  result =
    data && typeof data === "object" && !isRecursive
      ? JSON.stringify(data)
      : data;
  return result;
};

export const logoutPingUser = async () => {
  try {
    setSsoVerifyComplete(false);
    ssoTabSync.broadcastLogout();
    await getActiveUserManager().signoutRedirect({
      post_logout_redirect_uri: getSsoPostLogoutRedirectUri(),
    });
  } catch (error) {
    console.error("OIDC signoutRedirect failed:", error);
  }
};

export const isUserPresetAndAuthenticated = async () => {
  const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();
  const loggedInUserData = localStorage.getItem("loggedInUserData");
  try {
    if (isOAuthEnabled) {
      const accessToken = await getActiveUserManager().getUser();
      return Boolean(accessToken?.access_token && loggedInUserData);
    } else {
      return Boolean(TokenManager.get() && loggedInUserData);
    }
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const handleStorageClear = async (authState = null) => {
  try {
    const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();

    // Check if we're in an OIDC/SSO flow (oidc-client-ts stores its state in localStorage by default)
    const hasOidcInStorage = (() => {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || "";
        if (key.startsWith("oidc.") || key.includes("oidc")) return true;
      }
      return false;
    })();
    const urlParams = new URLSearchParams(window.location.search);
    const isOidcCallback = urlParams.has("code") && urlParams.has("state");
    const isInSsoFlow =
      authState?.isAuthenticated ||
      authState?.isLoading ||
      hasOidcInStorage ||
      isOidcCallback;

    //Don't clear storage if we're in SSO flow
    if (isInSsoFlow) {
      return;
    }

    if (isOAuthEnabled) {
      // Clear app-specific keys while preserving OIDC userManager storage
      const keysToPreserve = [];

      // Collect OIDC and Ping auth keys to preserve
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith("oidc.") ||
            key === PING_AUTH ||
            key === "home_link" ||
            key === OIDC_PROVIDER_STORAGE_KEY)
        ) {
          keysToPreserve.push(key);
        }
      }

      // Store values to preserve
      const preservedValues = keysToPreserve.map((key) => ({
        key,
        value: localStorage.getItem(key),
      }));

      // Clear all localStorage
      localStorage.clear();

      // Restore preserved values
      preservedValues.forEach(({ key, value }) => {
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      });

      // Set Ping authorization flag
      setPingAuthorizationFlag(true);
    } else {
      localStorage.clear();
    }
  } catch (error) {
    /* empty */
  }
};
